import { Button } from 'components/Form'
import { useCallback, useState, useRef } from 'react'
import { AiOutlineUpload, AiOutlineFile, AiOutlineClose } from 'react-icons/ai'
import { HiOutlineUpload } from 'react-icons/hi'

export default function Upload({
    onFilesSelected = () => {},
    showButton = false,
    error = false,
    multiple = false,
    message = '',
    id = '',
    label = '',
    className,
}) {
    const [files, setFiles] = useState([])
    const fileInputRef = useRef(null)
    const MAX_FILES = 5

    const handleFiles = (fileList) => {
        const selected = Array.from(fileList)

        const totalFiles = [...files, ...selected]
        if (totalFiles.length > MAX_FILES) {
            const remainingSlots = MAX_FILES - files.length
            const trimmed = selected.slice(0, remainingSlots)
            const updatedFiles = [...files, ...trimmed]
            setFiles(updatedFiles)
            onFilesSelected(updatedFiles)
        } else {
            const updatedFiles = [...files, ...selected]
            setFiles(updatedFiles)
            onFilesSelected(updatedFiles)
        }
    }

    const removeFile = (index) => {
        const updatedFiles = [...files]
        updatedFiles.splice(index, 1)
        setFiles(updatedFiles)
        onFilesSelected(updatedFiles)
    }

    const onDrop = useCallback(
        (e) => {
            e.preventDefault()
            if (e.dataTransfer.files?.length > 0) {
                handleFiles(e.dataTransfer.files)
                e.dataTransfer.clearData()
            }
        },
        // eslint-disable-next-line
        [files],
    )

    const onFileChange = (e) => {
        if (e.target.files?.length > 0) {
            handleFiles(e.target.files)
        }
    }

    const triggerFileInput = () => {
        if (files.length >= MAX_FILES) return
        fileInputRef.current?.click()
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {label && (
                <label
                    htmlFor={id}
                    className={`block mb-1 font-medium ${error ? 'text-danger' : 'text-black dark:text-white'}`}
                >
                    {label}
                </label>
            )}
            {!showButton && (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={triggerFileInput}
                    className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition ${
                        files.length >= MAX_FILES
                            ? 'border-gray-300 bg-gray-100 dark:bg-boxdark dark:border-gray-600 cursor-not-allowed opacity-50'
                            : 'border-gray-400 hover:border-blue-500 bg-white dark:bg-boxdark dark:border-gray-600'
                    }`}
                >
                    <AiOutlineUpload
                        size={40}
                        className="text-gray-500 dark:text-gray-300 mb-2"
                    />
                    <p className="text-gray-600 dark:text-gray-300">
                        Drag & drop files here
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-400">
                        or click to select files
                    </p>
                    {files.length >= MAX_FILES && (
                        <p className="text-sm text-danger mt-2">
                            Maximum 5 files allowed
                        </p>
                    )}
                </div>
            )}

            {showButton && (
                <Button
                    onClick={triggerFileInput}
                    className="text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                    disabled={files.length >= MAX_FILES}
                >
                    <HiOutlineUpload size={20} /> Upload
                </Button>
            )}

            <input
                ref={fileInputRef}
                type="file"
                multiple={multiple}
                className="hidden"
                onChange={onFileChange}
            />
            {error && <p className="text-sm text-danger">{message}</p>}

            {files.length > 0 && (
                <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-white">
                        Selected Files:
                    </p>
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        {files.map((file, index) => (
                            <li
                                key={index}
                                className="flex items-center justify-between gap-2"
                            >
                                {multiple
                                    ? multiple
                                    : index === files.length - 1 && (
                                          <div className="flex items-center gap-2">
                                              <AiOutlineFile />
                                              {file.name}
                                          </div>
                                      )}
                                {multiple && (
                                    <button
                                        onClick={() => removeFile(index)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Remove"
                                    >
                                        <AiOutlineClose />
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
